import { isBoolTrue, createErrorObject } from "../../components/ImportWizard/ImportWizard.utils";
import { decimalFromStr } from "../../infra/utils";
import { DiscountModel, serviceModel, serviceTemplateModel } from "../../models";
import { ServiceCostType } from "../../models/service-cost";
import { ServiceTemplateBundlePricingTypeEnum } from "../../models/service-template-bundle";
export const pricingOptions = ['fixed', 'hourly', 'quantity', 'range'];
export const discountTypeOptions = ['flat', 'percent'];
export const billingOccurrenceOptions = ['one time', 'in stages', 'weekly', 'biweekly', 'monthly', 'quarterly', 'yearly'];
export const bundleRelatedColumns = ['bundleDescription', 'bundleBillingOccurrence', 'bundlePricing', 'bundleUnitName', 'bundleUnitCap'];
export const bundleColumnsToValidate = [...bundleRelatedColumns, 'automaticBilling', 'discount', 'discountType'];
export const agreementBundleColumnsToValidate = [...bundleColumnsToValidate, 'billingDay', 'optionalService', 'issueInvoiceOnAcceptance', 'startPaused', 'priceIncreaseRate', 'priceIncreaseStartsOn', 'billedUpfront'];
const buildPossibleValuesErrorMessage = (fieldName, values, options) => {
  const supportedValues = values.filter(o => {
    var _options$excludeValue;
    return !(options !== null && options !== void 0 && (_options$excludeValue = options.excludeValues) !== null && _options$excludeValue !== void 0 && _options$excludeValue.includes(o));
  });
  const last = supportedValues.pop();
  let errorMessage = "Invalid entry. Please select a ".concat(fieldName, ":");
  if (supportedValues.length > 0) {
    supportedValues.forEach(o => {
      errorMessage += " <i>".concat(o, "</i>,");
    });
    return errorMessage + " or <i>".concat(last, "</i>");
  }
  return errorMessage + " <i>".concat(last, "</i> or leave it empty");
};
export const errorMessages = {
  incompatiblePriceAndPricingMsg: 'Invalid entry. When pricing is set to Range, you must specify a positive number for max rate or both min and max rate, for example 100-500',
  recurrencePeriodYearlyError: '<i>Yearly</i> cannot be selected when "Automatic Billing" is set to <i>Yes</i>. You can set "Automatic Billing" to <i>No</i> and "Billing occurrence" to <i>Yearly</i> or choose a different "Billing occurrence"',
  fixedPriceAutomaticServiceError: 'Invalid entry. Pricing must be <i>Fixed</i> for an automatic service',
  priceError: ' Invalid entry. Enter a positive number only, without any special symbols or characters.',
  rangePriceError: 'Invalid entry. When pricing is set to Range, you must specify a positive number for max rate or both min and max rate, for example <i>100-500</i>',
  positiveError: 'Invalid entry. Please specify a positive number',
  nonUniqueServiceNameError: 'Invalid entry. This service name already exists in your Service Library. Please choose a different name',
  nonUniqueBundleNameError: 'Invalid entry. This bundle name already exists in your Library. Please choose a different name',
  minPriceExceedingError: "Min price can't exceed max price. Please enter a lower value for min price or a higher value for max price",
  percentDiscountLargerThan100: 'Invalid entry. A percent discount cannot be greater than 100. Please enter a value equal to or less than 100',
  flatDiscountGreaterThanPrice: 'Invalid entry. A flat discount cannot be greater than the price. Please enter a flat discount value equal to or less than the price',
  bundleNameIsRequired: 'Bundle name is required when bundle settings are provided',
  bundleBillingOccurrenceIsRequired: 'Bundle billing occurrence is required when bundle name is set',
  bundlePricingIsRequired: 'Bundle pricing is required when bundle name is set',
  getBundleUniqueServicesError: (bundleName, serviceName) => "There are multiple services named \u201C".concat(serviceName, "\u201D included in bundle \u201C").concat(bundleName, "\u201D. Each included service must have a unique name within the bundle. Please rename the duplicates"),
  getBundleMustHave2ServicesError: bundleName => "Bundle \"".concat(bundleName, "\" must include at least two services"),
  getEmptyStringError: fieldName => "Invalid entry. ".concat(fieldName, " must have a value"),
  getInvalidBooleanError: _fieldName => 'invalid entry. Please ensure this column value is set to either "Yes" or "No".',
  getPricingError: function getPricingError() {
    let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
      excludeValues: []
    };
    return buildPossibleValuesErrorMessage('Pricing', pricingOptions, options);
  },
  getBillingOccurrenceError: function getBillingOccurrenceError() {
    let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
      excludeValues: []
    };
    return buildPossibleValuesErrorMessage('billing occurrence', billingOccurrenceOptions, options);
  },
  getDiscountTypeError: function getDiscountTypeError() {
    let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
      excludeValues: []
    };
    return buildPossibleValuesErrorMessage('Discount type', discountTypeOptions, options);
  },
  getBundleColumnsConsistencyError: (bundleName, inconsistentColumns) => "Bundle \"".concat(bundleName, "\" has inconsistent values in: ").concat(inconsistentColumns.join(', '))
};
export const isRowOfBundle = rowObject => !!(rowObject.bundleName || bundleRelatedColumns.some(column => rowObject[column]));
export const getPricingValidations = function getPricingValidations() {
  let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
    excludeValues: []
  };
  return {
    validate: (pricing, _ref) => {
      let {
        rowObject
      } = _ref;
      if (options !== null && options !== void 0 && options.isOptional && !pricing) return true;
      const normalized = pricing.toLowerCase();
      const validPricing = pricingOptions.includes(normalized) && !options.excludeValues.includes(normalized);
      if (!validPricing) return {
        errorType: 'pricingError'
      };
      const isNonFixedPrice = normalized !== 'fixed';
      const isAutomatic = options.actLikeManualIfBundledService && isRowOfBundle(rowObject) ? false : isBoolTrue(rowObject.automaticBilling);
      if (isNonFixedPrice && isAutomatic) return {
        errorType: 'nonFixedAutomatic'
      };
      return true;
    },
    validateError: (headerName, rowNumber, errorType) => {
      const errorMsg = errorType === 'pricingError' ? errorMessages.getPricingError(options) : errorType === 'nonFixedAutomatic' ? errorMessages.fixedPriceAutomaticServiceError : '';
      if (!errorMsg) return;
      return createErrorObject(headerName, rowNumber, errorMsg);
    }
  };
};
export const getBillingOccurrenceValidations = function getBillingOccurrenceValidations() {
  let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
    excludeValues: []
  };
  return {
    validate: (recurrencePeriod, _ref2) => {
      let {
        rowObject
      } = _ref2;
      if (options !== null && options !== void 0 && options.isOptional && !recurrencePeriod) return true;
      const {
        automaticBilling
      } = rowObject;
      const isAutomatic = options.actLikeManualIfBundledService && isRowOfBundle(rowObject) ? false : isBoolTrue(automaticBilling);
      const normalizedRecurrencePeriod = recurrencePeriod === null || recurrencePeriod === void 0 ? void 0 : recurrencePeriod.toLowerCase();
      const isValidRecurrencePeriod = billingOccurrenceOptions.includes(normalizedRecurrencePeriod) && !options.excludeValues.includes(normalizedRecurrencePeriod);
      if (!isValidRecurrencePeriod) return {
        valid: false,
        errorType: 'invalidValue'
      };
      const isYearly = normalizedRecurrencePeriod === 'yearly';
      return {
        valid: !(isYearly && isAutomatic),
        errorType: 'yearlyAndAutomatic'
      };
    },
    validateError: (headerName, rowNumber, errorType) => {
      switch (errorType) {
        case 'invalidValue':
          return createErrorObject(headerName, rowNumber, errorMessages.getBillingOccurrenceError(options));
        case 'yearlyAndAutomatic':
          return createErrorObject(headerName, rowNumber, errorMessages.recurrencePeriodYearlyError);
      }
    }
  };
};
export const getUnitCapValidations = function getUnitCapValidations() {
  let _options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
    excludeValues: []
  };
  return {
    validate: unitCap => {
      if ((unitCap === null || unitCap === void 0 ? void 0 : unitCap.length) === 0) return true;
      const decimal = decimalFromStr(unitCap);
      if (!decimal) return false;
      const isPositiveNumber = decimal.isPositive();
      const isNonZero = !decimal.isZero();
      return isPositiveNumber && isNonZero;
    },
    validateError: (headerName, rowNumber) => createErrorObject(headerName, rowNumber, errorMessages.positiveError)
  };
};
export const getServiceNameValidations = function getServiceNameValidations() {
  let {
    isBundleName
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return {
    validate: serviceName => {
      if (isBundleName && !serviceName) return {
        valid: true
      };
      const error = serviceModel.validateName(serviceName, {
        isBundleName
      });
      return error ? {
        valid: false,
        errorType: error
      } : {
        valid: true
      };
    },
    validateError: (headerName, rowNumber, errorType) => {
      if (errorType) {
        return createErrorObject(headerName, rowNumber, errorType);
      }
    }
  };
};
export const getBundlePricingType = row => {
  if (row.bundlePricing.toLowerCase() === 'fixed') {
    return {
      type: ServiceTemplateBundlePricingTypeEnum.Fixed
    };
  } else {
    return {
      type: ServiceTemplateBundlePricingTypeEnum.Variable,
      variable: {
        cap: row.bundleUnitCap ? decimalFromStr(row.bundleUnitCap) : null,
        name: row.bundleUnitName || null
      }
    };
  }
};
export const getServiceCost = function getServiceCost(row) {
  var _decimalFromStr;
  let {
    ignoreServiceDiscount,
    useBundleFields,
    customRate
  } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
    ignoreServiceDiscount: false,
    useBundleFields: false
  };
  const [pricingField, unitCapField, unitNameField] = useBundleFields ? ['bundlePricing', 'bundleUnitCap', 'bundleUnitName'] : ['pricing', 'unitCap', 'unitName'];
  const rate = customRate !== null && customRate !== void 0 ? customRate : row.rate;
  const pricing = row[pricingField].toLowerCase();
  const pricingType = pricing === 'fixed' ? ServiceCostType.Fixed : pricing === 'range' ? ServiceCostType.Range : ServiceCostType.Variable;
  const discountAmount = row.discount && !Number.isNaN(+row.discount) ? +row.discount : null;
  const hasMinPrice = pricingType === 'range' && rate.includes('-');
  const minPrice = hasMinPrice && ((_decimalFromStr = decimalFromStr(rate.split('-')[0])) === null || _decimalFromStr === void 0 ? void 0 : _decimalFromStr.toDP(2).toNumber()) || null;
  const price = decimalFromStr(hasMinPrice ? rate.split('-')[1] : rate).toDP(2);
  const serviceDiscount = !ignoreServiceDiscount && discountAmount != null ? row.discountType.toLowerCase() === 'percent' ? DiscountModel.makePercent(discountAmount) : DiscountModel.makeValue(discountAmount) : null;
  switch (pricingType) {
    case ServiceCostType.Fixed:
      return serviceTemplateModel.makeFixedCost(price, {
        discount: serviceDiscount
      });
    case ServiceCostType.Variable:
      {
        const decimalUnitCap = decimalFromStr(row[unitCapField]);
        return serviceTemplateModel.makeVariableCost(price, {
          unitCap: decimalUnitCap || 0,
          unitName: row[unitNameField] || null,
          discount: serviceDiscount
        });
      }
    case ServiceCostType.Range:
      return serviceTemplateModel.makeRangeCost(price, {
        minPrice,
        discount: serviceDiscount
      });
  }
};